import { DATE_FORMAT_USER, EMPTY_FILTER_VALUE } from 'admin/src/components/ui/DataGrid/filters/agGridAdapter';
import type { ActiveFilter, FilterOption } from 'admin/src/components/ui/DataGrid/filters/types';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';

export const INLINE_VALUE_LIMIT = 2;

export function filterValueLabel(value: string, options: FilterOption[]): string {
  return options.find((option) => option.value === value)?.label ?? (value === EMPTY_FILTER_VALUE ? 'Empty' : value);
}

export function formatFilterValue(filter: ActiveFilter, options: FilterOption[]): ReactNode {
  const { dataType } = filter.column;
  const { values } = filter;

  if (!values.length) return null;

  if (dataType === 'boolean') {
    if (values[0] === EMPTY_FILTER_VALUE) return <span className="italic text-muted-foreground">Empty</span>;
    return values[0] === 'true' ? 'Yes' : 'No';
  }

  if (dataType === 'number') {
    return <span className="tabular-nums tracking-tight">{values.length > 1 ? `${values[0]} – ${values[1]}` : values[0]}</span>;
  }

  if (dataType === 'date') {
    return formatDateRange(values[0], values[1]);
  }

  if (values.length > INLINE_VALUE_LIMIT) return `${values.length} selected`;

  return values.map((value, index) => {
    const label = filterValueLabel(value, options);

    return (
      <span key={value}>
        {index ? <span className="text-muted-foreground">, </span> : null}
        {value === EMPTY_FILTER_VALUE ? <span className="italic text-muted-foreground">{label}</span> : label}
      </span>
    );
  });
}

/** Collapses the parts a range shares, so a same-month range reads "Mar 3 – 14, 2026". */
export function formatDateRange(from: string, to?: string): string {
  const start = dayjs(from);

  if (!to) return start.format(DATE_FORMAT_USER);

  const end = dayjs(to);

  if (start.isSame(end, 'day')) return start.format(DATE_FORMAT_USER);
  if (start.isSame(end, 'month')) return `${start.format('MMM D')} – ${end.format('D, YYYY')}`;
  if (start.isSame(end, 'year')) return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;

  return `${start.format(DATE_FORMAT_USER)} – ${end.format(DATE_FORMAT_USER)}`;
}
